import { useState, useMemo, PropsWithChildren } from 'react';

import type {
  NotificationType,
  ContextValue,
  OpenNotificationOptions,
} from './Notification.types';

import { NotificationContext } from './NotificationContext';

import Dialog from 'shared/components/Dialog';
import Notification from './Notification';

export const NotificationProvider = ({ children }: PropsWithChildren<{}>) => {
  const [notificationInfo, setNotificationInfo] = useState<{
    open: boolean;
    title: string | null;
    description: string | null;
    type: NotificationType | null;
  }>({
    open: false,
    title: null,
    description: null,
    type: null,
  });

  const openNotification = (options: OpenNotificationOptions) => {
    setNotificationInfo({
      description: options.description,
      open: true,
      title: options.title ?? null,
      type: options.type,
    });
  };

  const closeNotification = () => {
    setNotificationInfo((prevState) => ({
      ...prevState,
      open: false,
    }));
  };

  const resetNotificationInfo = () => {
    setNotificationInfo({
      description: null,
      open: false,
      type: null,
      title: null,
    });
  };

  const contextValue = useMemo(
    () =>
      ({
        open: openNotification,
        close: closeNotification,
      } as ContextValue),
    []
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <Dialog
        fullWidth
        maxWidth="sm"
        open={notificationInfo.open}
        onClose={closeNotification}
        TransitionProps={{
          onExited: resetNotificationInfo,
        }}
      >
        <Notification
          description={notificationInfo.description}
          title={notificationInfo.title}
          type={notificationInfo.type}
          onClose={closeNotification}
        />
      </Dialog>
    </NotificationContext.Provider>
  );
};
